import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError, runAiJob } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { refreshGlossary } from "./GlossaryText";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { BookOpen, PlusCircle, PencilSimple, Trash, Sparkle, MagnifyingGlass, UploadSimple, ArrowsClockwise, CaretDown, CaretRight, FilePdf, Lightbulb, FloppyDisk } from "@phosphor-icons/react";

const TIMEFRAMES = [
  ["upcoming", "Upcoming changes"],
  ["next_12m", "Next 12 months"],
  ["past_12m", "Past 12 months"],
  ["all", "All changes"],
];

const STATUSES = [
  ["currently_enforceable", "Currently Enforceable"],
  ["future_enforceable", "Future Enforceable"],
  ["superseded", "Superseded"],
  ["retired", "Retired"],
];
const STATUS_CLS = {
  currently_enforceable: "bg-green-50 text-green-700 border-green-300",
  future_enforceable: "bg-blue-50 text-blue-700 border-blue-300",
  superseded: "bg-amber-50 text-amber-700 border-amber-300",
  retired: "bg-secondary text-gray-500 border-gray-300",
};
const STATUS_LABEL = Object.fromEntries(STATUSES);
const blank = { term: "", definition: "", effective_date: "", status: "currently_enforceable", standard_ids: [], source: "NERC Glossary of Terms Used in NERC Reliability Standards", notes: "", change_summary: "", is_new: false, is_revised: false, scope: "global" };

export default function DefinitionsRegister() {
  const { canEdit, aiEnabled } = useAuth();
  const [defs, setDefs] = useState(null);
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [staging, setStaging] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [timeframe, setTimeframe] = useState("upcoming");
  const [exporting, setExporting] = useState(false);
  const [upcomingOnly, setUpcomingOnly] = useState(false);
  const [scopeFilter, setScopeFilter] = useState("all");   // all | global (NERC) | entity
  const [needsDefOnly, setNeedsDefOnly] = useState(false);
  const [sectionOpen, setSectionOpen] = useState(false);
  const [batchAi, setBatchAi] = useState(false);
  const fileRef = useRef(null);
  const entityFileRef = useRef(null);
  const [entityUploading, setEntityUploading] = useState(false);
  const [entityReview, setEntityReview] = useState(null);   // { filename, counts, total, items }
  const [committingEntity, setCommittingEntity] = useState(false);
  const [seedingStarter, setSeedingStarter] = useState(false);
  const [genDef, setGenDef] = useState(null);          // id being AI-drafted (single)
  const [savingSug, setSavingSug] = useState(null);    // id being approved
  const [batchDef, setBatchDef] = useState(false);     // bulk AI definition job running
  const [suggestDraft, setSuggestDraft] = useState({});// editable suggestion text per id

  const load = useCallback(() => { api.get("/definitions").then((r) => { setDefs(r.data); refreshGlossary(); }).catch(() => setDefs([])); }, []);
  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      // Runs as a background job (poll) so large PDFs don't hit the proxy/Cloudflare timeout.
      const data = await runAiJob("/glossary/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStaging(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Glossary import failed."); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const commitStaging = async () => {
    if (!staging) return;
    setCommitting(true);
    try {
      const data = await runAiJob(`/glossary/staging/${staging.import_id}/commit`, {});
      toast.success(`Applied ${data.applied.new} new + ${data.applied.revised} revised term(s)`);
      setStaging(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Commit failed."); }
    finally { setCommitting(false); }
  };
  // ---- company (entity-specific) glossary upload ----
  const onEntityUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setEntityUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      toast.message("Parsing company glossary…");
      const data = await runAiJob("/entity-glossary/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // seed each item's chosen definition + import flag for the review editor
      data.items = data.items.map((it) => ({
        ...it,
        chosen: it.verdict === "duplicate" ? "uploaded" : "uploaded",
        definition: it.uploaded_definition,
        include: it.verdict !== "unchanged",
      }));
      setEntityReview(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Glossary import failed."); }
    finally { setEntityUploading(false); if (entityFileRef.current) entityFileRef.current.value = ""; }
  };
  const setEntityItem = (i, patch) => setEntityReview((r) => ({
    ...r, items: r.items.map((it, k) => (k === i ? { ...it, ...patch } : it)),
  }));
  const commitEntityGlossary = async () => {
    if (!entityReview) return;
    const items = entityReview.items.filter((it) => it.include && it.definition?.trim())
      .map((it) => ({ term: it.term, definition: it.definition, effective_date: it.effective_date || null, notes: it.notes || "" }));
    if (items.length === 0) { toast.error("Nothing selected to import."); return; }
    setCommittingEntity(true);
    try {
      const { data } = await api.post("/entity-glossary/commit", { items });
      toast.success(`Company glossary: ${data.applied.created} added, ${data.applied.updated} updated`);
      setEntityReview(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Commit failed."); }
    finally { setCommittingEntity(false); }
  };
  const seedStarter = async () => {
    if (!window.confirm("Load a starter set of common company-defined terms (Annual, Vendor, Contractor, Calendar Year, etc.) for this entity? Existing terms are skipped; all are added blank for you to define.")) return;
    setSeedingStarter(true);
    try {
      const { data } = await api.post("/entity-glossary/seed-starter", {});
      toast.success(`Loaded ${data.created} starter term(s)${data.skipped ? `, skipped ${data.skipped} existing` : ""}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed to load starter terms."); }
    finally { setSeedingStarter(false); }
  };
  const [expanded, setExpanded] = useState({});
  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));
  const [cmp, setCmp] = useState({});
  const toggleCmp = (id) => setCmp((e) => ({ ...e, [id]: !e[id] }));
  const [explaining, setExplaining] = useState(null);
  const explainChange = async (d) => {
    const targetId = d.upcoming?.id || d.id;
    setExplaining(d.id);
    try {
      const { data } = await api.post(`/definitions/${targetId}/explain-change`);
      setDefs((cur) => (cur || []).map((x) => (x.id === d.id
        ? { ...x, upcoming: x.upcoming ? { ...x.upcoming, change_summary: data.change_summary } : x.upcoming, change_summary: data.change_summary }
        : x)));
      toast.success("Change explanation generated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setExplaining(null); }
  };

  // ---- SME "How to apply" explanation (Claude Sonnet 4.5) ----
  const [smeGenerating, setSmeGenerating] = useState(null);   // def id currently being AI-generated
  const [smeEditor, setSmeEditor] = useState(null);           // { def, targetId, text, isUpcoming } or null
  const [smeSaving, setSmeSaving] = useState(false);
  const [smeBatch, setSmeBatch] = useState(false);

  const applySmeToLocal = (defId, targetId, text, edited) => {
    setDefs((cur) => (cur || []).map((x) => {
      if (x.id !== defId) return x;
      const isUpcoming = x.upcoming && x.upcoming.id === targetId;
      if (isUpcoming) {
        return { ...x, upcoming: { ...x.upcoming, sme_explanation: text, sme_explanation_edited: edited } };
      }
      return { ...x, sme_explanation: text, sme_explanation_edited: edited };
    }));
  };

  const generateSmeExplanation = async (d, opts = { upcoming: false }) => {
    const targetId = opts.upcoming && d.upcoming ? d.upcoming.id : d.id;
    setSmeGenerating(d.id + (opts.upcoming ? ":u" : ""));
    try {
      const { data } = await api.post(`/definitions/${targetId}/generate-explanation`);
      applySmeToLocal(d.id, targetId, data.sme_explanation, false);
      toast.success("SME explanation generated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI explanation failed."); }
    finally { setSmeGenerating(null); }
  };

  const openSmeEditor = (d, opts = { upcoming: false }) => {
    const isUpcoming = !!(opts.upcoming && d.upcoming);
    setSmeEditor({
      def: d,
      targetId: isUpcoming ? d.upcoming.id : d.id,
      isUpcoming,
      text: (isUpcoming ? d.upcoming?.sme_explanation : d.sme_explanation) || "",
    });
  };

  const saveSmeExplanation = async () => {
    if (!smeEditor) return;
    setSmeSaving(true);
    try {
      const { data } = await api.put(`/definitions/${smeEditor.targetId}/explanation`, { explanation: smeEditor.text });
      applySmeToLocal(smeEditor.def.id, smeEditor.targetId, data.sme_explanation, !!data.sme_explanation);
      toast.success("SME explanation saved");
      setSmeEditor(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setSmeSaving(false); }
  };

  const generateAllSme = async () => {
    setSmeBatch(true);
    try {
      const { data } = await api.post("/definitions/generate-explanations-all");
      const jobId = data.job_id;
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/ai/jobs/${jobId}`);
          if (j.status === "done") {
            clearInterval(poll); setSmeBatch(false); load();
            toast.success(`Generated ${j.result?.generated || 0} SME explanation(s)`);
          } else if (j.status === "error") {
            clearInterval(poll); setSmeBatch(false); toast.error("AI batch failed: " + (j.error || ""));
          }
        } catch { clearInterval(poll); setSmeBatch(false); }
      }, 4000);
    } catch (e) { setSmeBatch(false); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openNew = () => { setForm(blank); setEditId(null); setDialog(true); };
  const openEdit = (d) => {
    setForm({ ...blank, ...d, effective_date: d.effective_date || "", standard_ids: d.standard_ids || [] });
    setEditId(d.id); setDialog(true);
  };
  const save = async () => {
    if (!form.term.trim()) return toast.error("Term is required");
    const payload = {
      ...form, effective_date: form.effective_date || null,
      standard_ids: typeof form.standard_ids === "string"
        ? form.standard_ids.split(/[,\n]/).map((x) => x.trim()).filter(Boolean) : form.standard_ids,
    };
    try {
      if (editId) await api.put(`/definitions/${editId}`, payload);
      else await api.post("/definitions", payload);
      toast.success("Definition saved"); setDialog(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Delete the term "${d.term}"?`)) return;
    try { await api.delete(`/definitions/${d.id}`); load(); toast.success("Definition deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/definitions/export-comparison?timeframe=${encodeURIComponent(timeframe)}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `NERC_Glossary_Changes_${timeframe}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Comparison PDF downloaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export failed"); }
    finally { setExporting(false); }
  };

  const generateAll = async () => {
    setBatchAi(true);
    try {
      const { data } = await api.post("/definitions/explain-changes-all");
      const jobId = data.job_id;
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/ai/jobs/${jobId}`);
          if (j.status === "done") {
            clearInterval(poll); setBatchAi(false); load();
            toast.success(`Generated ${j.result?.generated || 0} explanation(s)`);
          } else if (j.status === "error") {
            clearInterval(poll); setBatchAi(false); toast.error("AI batch failed: " + (j.error || ""));
          }
        } catch { clearInterval(poll); setBatchAi(false); }
      }, 4000);
    } catch (e) { setBatchAi(false); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const suggestDefinition = async (d) => {
    setGenDef(d.id);
    try {
      const { data } = await api.post(`/definitions/${d.id}/generate-definition`);
      setDefs((cur) => (cur || []).map((x) => (x.id === d.id ? { ...x, suggested_definition: data.suggested_definition } : x)));
      setSuggestDraft((s) => ({ ...s, [d.id]: data.suggested_definition }));
      setExpanded((e) => ({ ...e, [d.id]: true }));
      toast.success("AI drafted a definition — review and approve");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI draft failed."); }
    finally { setGenDef(null); }
  };
  const approveSuggestion = async (d) => {
    setSavingSug(d.id);
    try {
      const definition = suggestDraft[d.id] ?? d.suggested_definition;
      await api.post(`/definitions/${d.id}/approve-suggestion`, { definition });
      toast.success(`Definition approved for "${d.term}"`);
      setSuggestDraft((s) => { const n = { ...s }; delete n[d.id]; return n; });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Approve failed."); }
    finally { setSavingSug(null); }
  };
  const dismissSuggestion = async (d) => {
    try {
      await api.post(`/definitions/${d.id}/dismiss-suggestion`);
      setDefs((cur) => (cur || []).map((x) => (x.id === d.id ? { ...x, suggested_definition: "" } : x)));
      setSuggestDraft((s) => { const n = { ...s }; delete n[d.id]; return n; });
      toast.success("Suggestion dismissed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Dismiss failed."); }
  };
  const generateAllDefs = async () => {
    setBatchDef(true);
    try {
      const { data } = await api.post("/definitions/generate-definitions-all");
      const jobId = data.job_id;
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/ai/jobs/${jobId}`);
          if (j.status === "done") {
            clearInterval(poll); setBatchDef(false); load();
            toast.success(`AI drafted ${j.result?.generated || 0} definition(s) — review and approve each`);
          } else if (j.status === "error") {
            clearInterval(poll); setBatchDef(false); toast.error("AI batch failed: " + (j.error || ""));
          }
        } catch { clearInterval(poll); setBatchDef(false); }
      }, 4000);
    } catch (e) { setBatchDef(false); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/definitions/seed-nerc");
      toast.success(`Imported ${data.added} NERC Glossary term${data.added === 1 ? "" : "s"}`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSeeding(false); }
  };

  const ql = q.trim().toLowerCase();
  const isUpcomingDef = (d) => d.has_future_revision || d.status === "future_enforceable";
  const filtered = (defs || []).filter((d) =>
    (!ql || `${d.term} ${d.definition} ${(d.standard_ids || []).join(" ")}`.toLowerCase().includes(ql))
    && (!upcomingOnly || isUpcomingDef(d))
    && (!needsDefOnly || (d.scope === "entity" && !(d.definition || "").trim()))
    && (scopeFilter === "all" || (scopeFilter === "entity" ? d.scope === "entity" : d.scope !== "entity")));
  const upcomingCount = (defs || []).filter(isUpcomingDef).length;
  const entityCount = (defs || []).filter((d) => d.scope === "entity").length;
  const needsDefCount = (defs || []).filter((d) => d.scope === "entity" && !(d.definition || "").trim()).length;
  const nercCount = (defs || []).length - entityCount;

  return (
    <div className="border border-indigo-200 bg-indigo-50/40" data-testid="definitions-register">
      <button data-testid="definitions-section-toggle" onClick={() => setSectionOpen((v) => !v)} className="w-full flex items-center gap-2 px-5 py-3 border-b border-indigo-200 bg-indigo-100/40 text-left">
        {sectionOpen ? <CaretDown size={16} weight="bold" className="text-indigo-700 shrink-0" /> : <CaretRight size={16} weight="bold" className="text-indigo-700 shrink-0" />}
        <BookOpen size={18} weight="fill" className="text-indigo-700" />
        <h3 className="font-display font-bold text-[#071B3E]">Definitions &amp; Defined Terms</h3>
        <span className="text-xs text-muted-foreground hidden sm:inline">— NERC Glossary terms, with enforcement dates &amp; new/revised flags</span>
        {defs && <span className="ml-auto text-xs font-mono bg-indigo-700 text-white px-2 py-0.5">{defs.length}</span>}
        {!sectionOpen && <span className="text-[11px] text-indigo-700 font-semibold ml-2">Show</span>}
      </button>

      {sectionOpen && (<>
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2 px-5 py-2.5 border-b border-indigo-100">
          <Button data-testid="add-definition-btn" onClick={openNew} size="sm" className="rounded-none bg-[#071B3E] hover:bg-[#0c2a5e]">
            <PlusCircle size={15} weight="fill" className="mr-1" />Add term
          </Button>
          <Button data-testid="seed-definitions-btn" onClick={seed} disabled={seeding} size="sm" variant="outline" className="rounded-none">
            <Sparkle size={14} weight="fill" className="mr-1" />{seeding ? "Importing…" : "Import NERC Glossary terms"}
          </Button>
          <input ref={fileRef} type="file" accept="application/pdf,.pdf" onChange={onUpload} className="hidden" data-testid="glossary-pdf-input" />
          <Button data-testid="upload-glossary-pdf-btn" onClick={() => fileRef.current?.click()} disabled={uploading} size="sm" variant="outline" className="rounded-none">
            <UploadSimple size={14} className="mr-1" />{uploading ? "Parsing PDF…" : "Upload Glossary PDF"}
          </Button>
          <input ref={entityFileRef} type="file" accept=".xlsx,.csv,.pdf,.docx" onChange={onEntityUpload} className="hidden" data-testid="entity-glossary-input" />
          <Button data-testid="upload-entity-glossary-btn" onClick={() => entityFileRef.current?.click()} disabled={entityUploading} size="sm" className="rounded-none bg-indigo-700 hover:bg-indigo-800 text-white">
            <UploadSimple size={14} className="mr-1" />{entityUploading ? "Parsing…" : "Upload Company Glossary"}
          </Button>
          <Button data-testid="seed-starter-terms-btn" onClick={seedStarter} disabled={seedingStarter} size="sm" variant="outline" className="rounded-none border-indigo-300 text-indigo-700">
            {seedingStarter ? "Loading…" : "Load starter terms"}
          </Button>
          <div className="ml-auto flex items-center gap-2">
            <div className="flex border border-indigo-300 rounded-none overflow-hidden text-xs" data-testid="glossary-scope-filter">
              {[["all", `All (${(defs || []).length})`], ["global", `NERC (${nercCount})`], ["entity", `Entity-only (${entityCount})`]].map(([val, label]) => (
                <button key={val} data-testid={`glossary-scope-${val}`} onClick={() => setScopeFilter(val)}
                  className={`px-2.5 py-1.5 font-semibold ${scopeFilter === val ? "bg-indigo-700 text-white" : "bg-white text-indigo-700 hover:bg-indigo-50"}`}>{label}</button>
              ))}
            </div>
            <div className="relative min-w-[200px]">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input data-testid="definitions-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search terms…"
                className="w-full border border-border bg-white pl-8 pr-2 py-1.5 text-sm rounded-none focus:outline-none focus:ring-1 focus:ring-primary" />
            </div>
          </div>
        </div>
      )}

      {defs && defs.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 px-5 py-2.5 border-b border-indigo-100 bg-white/50">
          <FilePdf size={16} weight="fill" className="text-indigo-700" />
          <span className="text-sm font-semibold text-[#071B3E]">Comparison PDF of changed terms</span>
          <span className="text-xs text-muted-foreground hidden sm:inline">— Current vs. Upcoming definitions</span>
          <div className="ml-auto flex items-center gap-2">
            <button data-testid="toggle-needs-def-only" onClick={() => { setNeedsDefOnly((v) => !v); if (!needsDefOnly) setScopeFilter("entity"); }}
              className={`text-xs font-semibold border px-2 py-1.5 rounded-none ${needsDefOnly ? "bg-amber-600 text-white border-amber-600" : "bg-white text-amber-700 border-amber-300 hover:bg-amber-50"}`}>
              {needsDefOnly ? "Showing terms needing definitions" : "Needs definition only"} ({needsDefCount})
            </button>
            {canEdit && needsDefCount > 0 && (
              <Button data-testid="generate-all-defs-btn" onClick={generateAllDefs} disabled={batchDef || !aiEnabled}
                title={!aiEnabled ? "AI is turned off for this entity" : "Draft AI definitions for every company term that needs one (you approve each)"}
                size="sm" variant="outline" className="rounded-none border-amber-300 text-amber-700 hover:bg-amber-50">
                <Sparkle size={14} weight="fill" className="mr-1" />{batchDef ? "Drafting…" : "AI-suggest all undefined"}
              </Button>
            )}
            <button data-testid="toggle-upcoming-only" onClick={() => setUpcomingOnly((v) => !v)}
              className={`text-xs font-semibold border px-2 py-1.5 rounded-none ${upcomingOnly ? "bg-orange-600 text-white border-orange-600" : "bg-white text-orange-700 border-orange-300 hover:bg-orange-50"}`}>
              {upcomingOnly ? "Showing upcoming changes" : "Show upcoming changes only"} ({upcomingCount})
            </button>
            {canEdit && (
              <Button data-testid="generate-all-ai-btn" onClick={generateAll} disabled={batchAi || !aiEnabled || upcomingCount === 0}
                title={!aiEnabled ? "AI is turned off for this entity" : "Auto-generate AI 'what changed' for all upcoming revisions"}
                size="sm" variant="outline" className="rounded-none">
                <Sparkle size={14} weight="fill" className="mr-1" />{batchAi ? "Generating…" : "Auto-populate AI descriptions"}
              </Button>
            )}
            {canEdit && (
              <Button data-testid="generate-all-sme-btn" onClick={generateAllSme} disabled={smeBatch || !aiEnabled}
                title={!aiEnabled ? "AI is turned off for this entity" : "Auto-generate plain-English SME 'How to apply' explanations for every term that doesn't have one"}
                size="sm" variant="outline" className="rounded-none border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                <Lightbulb size={14} weight="fill" className="mr-1" />{smeBatch ? "Generating…" : "AI: SME explanations"}
              </Button>
            )}
            <Select value={timeframe} onValueChange={setTimeframe}>
              <SelectTrigger data-testid="changes-timeframe-select" className="rounded-none h-8 w-[170px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{TIMEFRAMES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid="export-comparison-pdf-btn" onClick={exportPdf} disabled={exporting} size="sm" className="rounded-none bg-[#071B3E] hover:bg-[#0c2a5e]">
              <FilePdf size={14} weight="fill" className="mr-1" />{exporting ? "Generating…" : "Export PDF"}
            </Button>
          </div>
        </div>
      )}

      {defs && filtered.length > 0 && (
        <div className="flex items-center gap-2 px-5 py-2 border-b border-indigo-100 bg-white/30">
          <span className="text-[11px] text-muted-foreground">{filtered.length} term(s) · collapsed</span>
          <div className="ml-auto flex items-center gap-1">
            <button data-testid="expand-all-defs" onClick={() => setExpanded(Object.fromEntries(filtered.map((d) => [d.id, true])))}
              className="text-[11px] font-semibold text-indigo-700 border border-indigo-200 px-2 py-1 rounded-none hover:bg-indigo-50 inline-flex items-center gap-1">
              <CaretDown size={11} />Expand all
            </button>
            <button data-testid="collapse-all-defs" onClick={() => setExpanded({})}
              className="text-[11px] font-semibold text-indigo-700 border border-indigo-200 px-2 py-1 rounded-none hover:bg-indigo-50 inline-flex items-center gap-1">
              <CaretRight size={11} />Collapse all
            </button>
          </div>
        </div>
      )}

      {defs === null ? (
        <div className="px-5 py-4 text-sm text-muted-foreground">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="px-5 py-4 text-sm text-muted-foreground" data-testid="definitions-empty">
          {defs.length === 0 ? "No defined terms yet." : "No terms match your search."}
          {canEdit && defs.length === 0 ? " Use “Import NERC Glossary terms” to seed the register, then add enforcement dates and definitions." : ""}
        </div>
      ) : (
        <div className="divide-y divide-indigo-100">
          {filtered.map((d) => (
            <div key={d.id} data-testid={`definition-${d.id}`} className="px-5 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <button data-testid={`def-toggle-${d.id}`} onClick={() => toggleExpand(d.id)} className="flex items-center gap-2 flex-wrap text-left w-full">
                    {expanded[d.id] ? <CaretDown size={13} className="text-indigo-700 shrink-0" /> : <CaretRight size={13} className="text-indigo-700 shrink-0" />}
                    <span className="font-display font-bold text-sm text-[#071B3E]">{d.term}</span>
                    <span className={`text-[10px] uppercase tracking-wide border px-1.5 py-0.5 ${STATUS_CLS[d.status] || STATUS_CLS.currently_enforceable}`}>
                      {STATUS_LABEL[d.status] || d.status}{d.effective_date && d.status !== "retired" ? ` · ${d.effective_date}` : ""}
                    </span>
                    {(d.version_number || 1) > 1 && <span className="text-[10px] font-mono border border-border bg-white px-1.5 py-0.5 text-muted-foreground">v{d.version_number}</span>}
                    {d.is_new && <span data-testid={`def-new-${d.id}`} className="text-[10px] uppercase tracking-wide border border-emerald-300 bg-emerald-50 text-emerald-700 px-1.5 py-0.5">New</span>}
                    {d.has_future_revision && <span data-testid={`def-revised-${d.id}`} className="text-[10px] uppercase tracking-wide border border-orange-400 bg-orange-50 text-orange-700 px-1.5 py-0.5 font-bold">Revised · eff {d.upcoming?.effective_date}</span>}
                    {d.scope === "entity" && <span data-testid={`def-entity-${d.id}`} className="text-[10px] uppercase tracking-wide border border-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 px-1.5 py-0.5 font-bold">Entity-only</span>}
                    {d.scope === "entity" && !(d.definition || "").trim() && <span data-testid={`def-needs-${d.id}`} className="text-[10px] uppercase tracking-wide border border-amber-400 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 font-bold">Needs definition</span>}
                    {(d.standard_ids || []).map((sid) => (
                      <span key={sid} className="text-[10px] font-mono border border-border bg-white px-1.5 py-0.5 text-muted-foreground">{sid}</span>
                    ))}
                  </button>
                  {d.scope === "entity" && (d.suggested_definition ? (
                    <div className="ml-[21px] mt-2 border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2.5" data-testid={`suggestion-panel-${d.id}`}>
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Sparkle size={13} weight="fill" className="text-amber-600 dark:text-amber-400" />
                        <span className="text-[10px] uppercase font-bold tracking-wide text-amber-700 dark:text-amber-300">AI-suggested definition — review, edit &amp; approve</span>
                      </div>
                      <Textarea data-testid={`suggestion-text-${d.id}`} value={suggestDraft[d.id] ?? d.suggested_definition}
                        onChange={(e) => setSuggestDraft((s) => ({ ...s, [d.id]: e.target.value }))}
                        className="rounded-none text-sm min-h-[70px] bg-white dark:bg-background" />
                      {canEdit && (
                        <div className="flex items-center gap-2 mt-2">
                          <Button data-testid={`approve-suggestion-${d.id}`} onClick={() => approveSuggestion(d)} disabled={savingSug === d.id}
                            size="sm" className="rounded-none bg-emerald-600 hover:bg-emerald-700 h-7 text-xs">
                            {savingSug === d.id ? "Saving…" : "Approve & save"}
                          </Button>
                          <Button data-testid={`regen-suggestion-${d.id}`} onClick={() => suggestDefinition(d)} disabled={genDef === d.id || !aiEnabled}
                            size="sm" variant="outline" className="rounded-none h-7 text-xs">
                            <Sparkle size={11} weight="fill" className="mr-1" />{genDef === d.id ? "…" : "Regenerate"}
                          </Button>
                          <button data-testid={`dismiss-suggestion-${d.id}`} onClick={() => dismissSuggestion(d)}
                            className="text-xs font-semibold text-muted-foreground hover:text-red-600">Dismiss</button>
                        </div>
                      )}
                    </div>
                  ) : (canEdit && !(d.definition || "").trim() && (
                    <div className="ml-[21px] mt-1.5">
                      <button data-testid={`suggest-definition-${d.id}`} onClick={() => suggestDefinition(d)} disabled={genDef === d.id || !aiEnabled}
                        title={!aiEnabled ? "AI is turned off for this entity" : "Let AI draft a definition you can review and edit"}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 dark:text-indigo-300 border border-indigo-300 px-1.5 py-0.5 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 disabled:opacity-50 rounded-none">
                        <Sparkle size={11} weight="fill" />{genDef === d.id ? "Drafting…" : "AI: suggest definition"}
                      </button>
                    </div>
                  )))}
                  {expanded[d.id] && (
                  <div className="pl-[21px]">
                  {d.definition && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{d.definition}</p>}
                  {/* SME "How to apply" — current version */}
                  {d.definition && (
                    <div data-testid={`sme-panel-${d.id}`} className="mt-2 border-l-2 border-indigo-400 bg-indigo-50/70 pl-3 pr-2 py-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Lightbulb size={12} weight="fill" className="text-indigo-700" />
                        <span className="text-[10px] uppercase tracking-wide font-bold text-indigo-700">SME explanation — how to apply</span>
                        {d.sme_explanation_edited && <span className="text-[10px] uppercase tracking-wide border border-indigo-300 bg-white text-indigo-700 px-1 py-[1px]">Admin-edited</span>}
                        {canEdit && (
                          <div className="ml-auto flex items-center gap-1">
                            <button data-testid={`sme-generate-${d.id}`} onClick={() => generateSmeExplanation(d)}
                              disabled={smeGenerating === d.id || !aiEnabled}
                              title={!aiEnabled ? "AI is turned off for this entity" : "Generate SME explanation with Claude Sonnet 4.5"}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 border border-indigo-300 px-1.5 py-0.5 hover:bg-indigo-100 disabled:opacity-50">
                              <Sparkle size={11} weight="fill" />
                              {smeGenerating === d.id ? "Analyzing…" : (d.sme_explanation ? "Regenerate" : "AI: explain")}
                            </button>
                            <button data-testid={`sme-edit-${d.id}`} onClick={() => openSmeEditor(d)}
                              title="Edit or write the SME explanation"
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 border border-indigo-300 px-1.5 py-0.5 hover:bg-indigo-100">
                              <PencilSimple size={11} />Edit
                            </button>
                          </div>
                        )}
                      </div>
                      {d.sme_explanation ? (
                        <p data-testid={`sme-text-${d.id}`} className="text-xs text-foreground/85 mt-1.5 whitespace-pre-line leading-relaxed">{d.sme_explanation}</p>
                      ) : (
                        <p className="text-[11px] text-muted-foreground italic mt-1.5">
                          No SME explanation yet. {canEdit ? "Use \u201cAI: explain\u201d for a plain-English \u201chow to apply\u201d summary, or write your own." : "An admin has not added one yet."}
                        </p>
                      )}
                    </div>
                  )}
                  {d.has_future_revision && d.upcoming && (
                    <div className="mt-1.5 border-l-2 border-orange-400 bg-orange-50/50 pl-3 py-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wide font-bold text-orange-700">Upcoming revision · effective {d.upcoming.effective_date}</span>
                        <button data-testid={`view-previous-${d.id}`} onClick={() => toggleCmp(d.id)}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-700 hover:underline">
                          {cmp[d.id] ? <CaretDown size={12} /> : <CaretRight size={12} />}{cmp[d.id] ? "Hide" : "View"} comparison
                        </button>
                        {canEdit && (
                          <button data-testid={`explain-change-${d.id}`} onClick={() => explainChange(d)} disabled={explaining === d.id || !aiEnabled}
                            title={!aiEnabled ? "AI is turned off for this entity" : "Generate an AI explanation of the change"}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-700 border border-orange-300 px-1.5 py-0.5 hover:bg-orange-100 disabled:opacity-50">
                            <Sparkle size={11} weight="fill" />{explaining === d.id ? "Analyzing…" : (d.upcoming.change_summary ? "Regenerate (AI)" : "AI: explain change")}
                          </button>
                        )}
                      </div>
                      {d.upcoming.change_summary && (
                        <p data-testid={`change-summary-${d.id}`} className="text-xs text-foreground/80 mt-1"><span className="font-bold uppercase text-[10px] text-orange-700 mr-1">What changes:</span>{d.upcoming.change_summary}</p>
                      )}
                      {/* SME "How to apply" — upcoming revision */}
                      <div data-testid={`sme-upcoming-panel-${d.id}`} className="mt-2 border border-indigo-200 bg-white/70 px-2.5 py-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Lightbulb size={11} weight="fill" className="text-indigo-700" />
                          <span className="text-[10px] uppercase tracking-wide font-bold text-indigo-700">SME explanation — upcoming (post-effective)</span>
                          {d.upcoming.sme_explanation_edited && <span className="text-[10px] uppercase tracking-wide border border-indigo-300 bg-white text-indigo-700 px-1 py-[1px]">Admin-edited</span>}
                          {canEdit && (
                            <div className="ml-auto flex items-center gap-1">
                              <button data-testid={`sme-generate-upcoming-${d.id}`} onClick={() => generateSmeExplanation(d, { upcoming: true })}
                                disabled={smeGenerating === d.id + ":u" || !aiEnabled}
                                title={!aiEnabled ? "AI is turned off for this entity" : "Generate SME explanation for the upcoming revision"}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 border border-indigo-300 px-1.5 py-0.5 hover:bg-indigo-100 disabled:opacity-50">
                                <Sparkle size={11} weight="fill" />{smeGenerating === d.id + ":u" ? "…" : (d.upcoming.sme_explanation ? "Regenerate" : "AI: explain")}
                              </button>
                              <button data-testid={`sme-edit-upcoming-${d.id}`} onClick={() => openSmeEditor(d, { upcoming: true })}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 border border-indigo-300 px-1.5 py-0.5 hover:bg-indigo-100">
                                <PencilSimple size={11} />Edit
                              </button>
                            </div>
                          )}
                        </div>
                        {d.upcoming.sme_explanation ? (
                          <p data-testid={`sme-upcoming-text-${d.id}`} className="text-xs text-foreground/85 mt-1 whitespace-pre-line leading-relaxed">{d.upcoming.sme_explanation}</p>
                        ) : (
                          <p className="text-[11px] text-muted-foreground italic mt-1">No SME explanation for the upcoming revision yet.</p>
                        )}
                      </div>
                      {cmp[d.id] && (
                        <div data-testid={`previous-def-${d.id}`} className="mt-1.5 grid sm:grid-cols-2 gap-2 text-xs">
                          <div className="border border-border bg-white p-2">
                            <div className="font-bold text-[10px] uppercase text-green-700 mb-0.5">Current · eff {d.effective_date || "\u2014"}</div>
                            <p className="text-muted-foreground">{d.definition}</p>
                          </div>
                          <div className="border border-orange-300 bg-orange-50/60 p-2">
                            <div className="font-bold text-[10px] uppercase text-orange-700 mb-0.5">Upcoming · eff {d.upcoming.effective_date}</div>
                            <p className="text-foreground/80">{d.upcoming.definition}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {d.source && <p className="text-[11px] text-muted-foreground/70 mt-1 italic">{d.source}</p>}
                  </div>
                  )}
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button data-testid={`edit-definition-${d.id}`} onClick={() => openEdit(d)} className="text-muted-foreground hover:text-foreground"><PencilSimple size={16} /></button>
                    <button data-testid={`delete-definition-${d.id}`} onClick={() => remove(d)} className="text-muted-foreground hover:text-red-600"><Trash size={16} /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      </>)}

      {/* SME "How to apply" editor */}
      <Dialog open={!!smeEditor} onOpenChange={(v) => { if (!v) setSmeEditor(null); }}>
        <DialogContent className="rounded-none max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb size={18} weight="fill" className="text-indigo-700" />
              SME explanation {smeEditor?.isUpcoming ? "(upcoming revision)" : ""}
            </DialogTitle>
          </DialogHeader>
          {smeEditor && (
            <div className="space-y-3">
              <div>
                <div className="text-xs font-semibold text-[#071B3E]">{smeEditor.def.term}</div>
                <div className="text-[11px] text-muted-foreground italic mt-0.5 max-h-24 overflow-y-auto whitespace-pre-line border-l-2 border-border pl-2">
                  {smeEditor.isUpcoming ? smeEditor.def.upcoming?.definition : smeEditor.def.definition}
                </div>
              </div>
              <div>
                <Label className="text-xs">Plain-English explanation — how an SME would apply this</Label>
                <Textarea data-testid="sme-editor-text" value={smeEditor.text}
                  onChange={(e) => setSmeEditor({ ...smeEditor, text: e.target.value })}
                  placeholder="Write a 1-2 paragraph explanation, or click 'AI: generate' to have Claude Sonnet 4.5 draft one you can then edit."
                  className="rounded-none mt-1 min-h-[180px] text-sm" />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Saved explanations are marked <span className="font-semibold text-indigo-700">Admin-edited</span> and are not overwritten by AI batch runs.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button data-testid="sme-editor-generate" onClick={() => generateSmeExplanation(smeEditor.def, { upcoming: smeEditor.isUpcoming })}
                  disabled={!aiEnabled || smeGenerating === smeEditor.def.id + (smeEditor.isUpcoming ? ":u" : "")}
                  variant="outline" size="sm" className="rounded-none border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                  <Sparkle size={13} weight="fill" className="mr-1" />
                  {smeGenerating ? "Analyzing…" : "AI: generate / regenerate"}
                </Button>
                <div className="ml-auto flex items-center gap-2">
                  <Button data-testid="sme-editor-cancel" onClick={() => setSmeEditor(null)} variant="outline" size="sm" className="rounded-none">Cancel</Button>
                  <Button data-testid="sme-editor-save" onClick={saveSmeExplanation} disabled={smeSaving} size="sm" className="rounded-none bg-[#071B3E] hover:bg-[#0c2a5e]">
                    <FloppyDisk size={13} weight="fill" className="mr-1" />{smeSaving ? "Saving…" : "Save"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "Add"} Defined Term</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Term</Label><Input data-testid="def-term-input" value={form.term} onChange={(e) => setForm({ ...form, term: e.target.value })} placeholder="e.g. BES Cyber System" className="rounded-none mt-1" /></div>
            <div><Label>Definition</Label><Textarea data-testid="def-text-input" value={form.definition} onChange={(e) => setForm({ ...form, definition: e.target.value })} placeholder="Paste the public NERC Glossary definition…" className="rounded-none mt-1 min-h-[100px]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="def-status-select" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Enforcement Date</Label><Input data-testid="def-date-input" type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} className="rounded-none mt-1" /></div>
            </div>
            <div><Label>Linked Standards (comma separated)</Label>
              <Input data-testid="def-standards-input" value={Array.isArray(form.standard_ids) ? form.standard_ids.join(", ") : form.standard_ids}
                onChange={(e) => setForm({ ...form, standard_ids: e.target.value })} placeholder="CIP-002-5.1a, CIP-003-9" className="rounded-none mt-1" /></div>
            <div><Label>Source</Label><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="rounded-none mt-1" /></div>
            <div><Label>What changed (change summary)</Label>
              <Textarea data-testid="def-change-summary-input" value={form.change_summary} onChange={(e) => setForm({ ...form, change_summary: e.target.value })}
                placeholder="Plain-language explanation of how this definition changed vs. the previous version (or use AI: explain change)…" className="rounded-none mt-1 min-h-[60px]" /></div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input data-testid="def-is-new" type="checkbox" checked={form.is_new} onChange={(e) => setForm({ ...form, is_new: e.target.checked })} className="accent-emerald-600" />
                New (introduced by a future standard)
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input data-testid="def-is-revised" type="checkbox" checked={form.is_revised} onChange={(e) => setForm({ ...form, is_revised: e.target.checked })} className="accent-amber-600" />
                Revised
              </label>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer border-t border-border pt-3" title="Global terms are shared across every registered entity. Entity-specific terms are private to the entity you are currently viewing.">
              <input data-testid="def-scope-entity" type="checkbox" checked={form.scope === "entity"}
                onChange={(e) => setForm({ ...form, scope: e.target.checked ? "entity" : "global" })} className="accent-indigo-600" />
              Entity-specific term (private to this entity — not part of the shared NERC Glossary)
            </label>
            <Button data-testid="save-definition-btn" onClick={save} className="rounded-none w-full bg-primary hover:bg-primary/90">Save Definition</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF import staging review */}
      <Dialog open={!!staging} onOpenChange={(o) => { if (!o) setStaging(null); }}>
        <DialogContent className="rounded-none max-w-2xl">
          <DialogHeader><DialogTitle>Review Glossary Import — {staging?.filename}</DialogTitle></DialogHeader>
          {staging && (
            <div className="space-y-3">
              <div className="flex gap-3 text-sm" data-testid="staging-counts">
                <span className="border border-emerald-300 bg-emerald-50 text-emerald-700 px-2 py-1 font-semibold">{staging.counts.new} New</span>
                <span className="border border-orange-400 bg-orange-50 text-orange-700 px-2 py-1 font-semibold">{staging.counts.revised} Revised</span>
                <span className="border border-border bg-secondary px-2 py-1 text-muted-foreground">{staging.counts.unchanged} Unchanged</span>
                <span className="ml-auto text-muted-foreground self-center">{staging.total} terms parsed</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Collision check: existing terms with a changed definition are versioned (the old text is preserved and viewable). Unchanged terms are skipped. Committing applies all New + Revised terms.
              </p>
              <div className="max-h-[50vh] overflow-y-auto border border-border divide-y divide-border">
                {staging.items.filter((it) => it.verdict !== "unchanged").map((it, i) => (
                  <div key={i} data-testid={`staging-item-${i}`} className="px-3 py-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{it.term}</span>
                      {it.verdict === "new"
                        ? <span className="text-[10px] uppercase border border-emerald-300 bg-emerald-50 text-emerald-700 px-1.5 py-0.5">New</span>
                        : <span className="text-[10px] uppercase border border-orange-400 bg-orange-50 text-orange-700 px-1.5 py-0.5 font-bold">Revised</span>}
                      {it.effective_date && <span className="text-[10px] font-mono text-muted-foreground">eff {it.effective_date}</span>}
                    </div>
                    {it.verdict === "revised" && it.old_definition && (
                      <p className="text-xs text-muted-foreground mt-1 line-through decoration-orange-400/60">{it.old_definition}</p>
                    )}
                    <p className="text-xs mt-0.5">{it.definition}</p>
                  </div>
                ))}
                {staging.items.filter((it) => it.verdict !== "unchanged").length === 0 && (
                  <div className="px-3 py-4 text-sm text-muted-foreground">No new or revised terms — the glossary is already up to date.</div>
                )}
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button variant="outline" onClick={() => setStaging(null)} className="rounded-none">Cancel</Button>
                <Button data-testid="commit-glossary-btn" onClick={commitStaging} disabled={committing || (staging.counts.new + staging.counts.revised === 0)}
                  className="rounded-none bg-primary hover:bg-primary/90">
                  <ArrowsClockwise size={15} className="mr-1.5" />{committing ? "Applying…" : `Commit ${staging.counts.new + staging.counts.revised} term(s)`}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Company (entity-specific) glossary review */}
      <Dialog open={!!entityReview} onOpenChange={(o) => { if (!o) setEntityReview(null); }}>
        <DialogContent className="rounded-none max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Review Company Glossary — {entityReview?.filename}</DialogTitle></DialogHeader>
          {entityReview && (
            <div className="space-y-3" data-testid="entity-glossary-review">
              <p className="text-xs text-muted-foreground">Imported as <span className="font-semibold">entity-only</span> terms for the current entity. Duplicates of an existing term (yours or a NERC term) are flagged — pick which definition is correct.</p>
              <div className="flex gap-3 text-sm" data-testid="entity-glossary-counts">
                <span className="border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 px-2 py-1 font-semibold">{entityReview.counts.new} New</span>
                <span className="border border-amber-400 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 px-2 py-1 font-semibold">{entityReview.counts.duplicate} Duplicate</span>
                <span className="border border-border bg-secondary px-2 py-1 text-muted-foreground">{entityReview.counts.unchanged} Unchanged</span>
                <span className="ml-auto text-muted-foreground self-center">{entityReview.total} parsed</span>
              </div>
              <div className="border border-border divide-y divide-border max-h-[52vh] overflow-y-auto">
                {entityReview.items.filter((it) => it.verdict !== "unchanged").map((it, idx) => {
                  const i = entityReview.items.indexOf(it);
                  return (
                    <div key={i} data-testid={`entity-item-${idx}`} className="px-3 py-2 space-y-1.5">
                      <label className="flex items-center gap-2 font-display font-bold text-sm">
                        <input type="checkbox" data-testid={`entity-item-include-${idx}`} checked={it.include} onChange={(e) => setEntityItem(i, { include: e.target.checked })} className="accent-indigo-600" />
                        {it.term}
                        {it.verdict === "duplicate" && <span className="text-[10px] uppercase tracking-wide border border-amber-400 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 font-bold">duplicate term{it.existing_scope === "global" ? " · NERC" : ""}</span>}
                        {it.verdict === "new" && <span className="text-[10px] uppercase tracking-wide border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 font-bold">new</span>}
                      </label>
                      {it.verdict === "duplicate" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                          <label className={`border p-2 cursor-pointer ${it.chosen === "existing" ? "border-primary bg-secondary" : "border-border"}`}>
                            <input type="radio" className="mr-1 accent-indigo-600" checked={it.chosen === "existing"} data-testid={`entity-item-keep-${idx}`}
                              onChange={() => setEntityItem(i, { chosen: "existing", definition: it.existing_definition })} />
                            <span className="font-semibold">Existing{it.existing_scope === "global" ? " (NERC)" : ""}</span>
                            <p className="text-muted-foreground mt-1">{it.existing_definition || "—"}</p>
                          </label>
                          <label className={`border p-2 cursor-pointer ${it.chosen === "uploaded" ? "border-primary bg-secondary" : "border-border"}`}>
                            <input type="radio" className="mr-1 accent-indigo-600" checked={it.chosen === "uploaded"} data-testid={`entity-item-use-${idx}`}
                              onChange={() => setEntityItem(i, { chosen: "uploaded", definition: it.uploaded_definition })} />
                            <span className="font-semibold">Uploaded</span>
                            <p className="text-muted-foreground mt-1">{it.uploaded_definition || "—"}</p>
                          </label>
                        </div>
                      )}
                      <Textarea data-testid={`entity-item-def-${idx}`} value={it.definition} onChange={(e) => setEntityItem(i, { definition: e.target.value })}
                        placeholder="Definition" className="rounded-none min-h-[52px] text-sm" />
                    </div>
                  );
                })}
                {entityReview.items.filter((it) => it.verdict !== "unchanged").length === 0 && (
                  <div className="px-3 py-4 text-sm text-muted-foreground">No new or changed terms found — everything already matches this entity's glossary.</div>
                )}
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button variant="outline" onClick={() => setEntityReview(null)} className="rounded-none">Cancel</Button>
                <Button data-testid="commit-entity-glossary-btn" onClick={commitEntityGlossary} disabled={committingEntity}
                  className="rounded-none bg-indigo-700 hover:bg-indigo-800 text-white">
                  <UploadSimple size={15} className="mr-1.5" />{committingEntity ? "Saving…" : "Import selected"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
